import React from 'react'
import {useTranslation} from 'react-i18next'
import {QuoteType} from '@/features/quote/types.ts'
import {
    StyledPrice,
    StyledProductName,
    StyledGroupTitle,
    StyledGroupHeaderItem,
    StyledHeaderTitle,
    StyledHeader,
    StyledProductsSectionWrapper,
    StyledSectionTitle,
    StyledProductRow,
    StyledQuantity
} from './style'
import {formatValues} from '@/features/project-details/helpers/helpers'
import {correctCurrency} from '@/utilities/constants/user'
import {ComponentCategoriesE} from '@/features/catalog-components/services/catalogComponents.schema'
import {useTheme} from 'styled-components'
import {Divider} from '@/components/ui/divider/Divider'
import {ProductsTotalPrice} from '../product-total-price/ProductsTotalPrice'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'

interface ProductsSectionProps {
    quoteData: QuoteType
    isSimplifiedQuote?: boolean
}

interface ProductType {
    id: string
    name: string
    product?: {
        attributes: Array<{
            name: string
            pivot: {
                pivot_value_number: string | null
                pivot_value_string: string | null
            }
        }>
    }
    category: {
        slug: string
    }
    finalLinePrice: number
    unitPrice: number
    quantity: number
    slug: string
}

interface GroupType {
    title: string
    products: ProductType[]
}

export const ProductsSection: React.FC<ProductsSectionProps> = ({quoteData, isSimplifiedQuote = false}) => {
    const {t} = useTranslation()
    const theme = useTheme()

    const titleToRender = (category: string) => {
        switch (category) {
            case ComponentCategoriesE.Panel:
                return {
                    title: t('quote:components:panel')
                }
            case ComponentCategoriesE.Inverter:
                return {
                    title: t('quote:components:inverter')
                }

            case ComponentCategoriesE.Battery:
            case ComponentCategoriesE.CombinerBox:
                return {
                    title: t('quote:components:battery')
                }
            case ComponentCategoriesE.Cables:
                return {
                    title: t('quote:components:accessories_panels')
                }
            case ComponentCategoriesE.WallBattery:
            case ComponentCategoriesE.Optimizer:
            case ComponentCategoriesE.Connectors:
                return {
                    title: t('quote:components:accessories_panels')
                }
            case ComponentCategoriesE.PowerDistributionPanelsAC:
            case ComponentCategoriesE.PowerDistributionPanelsDC:
            case ComponentCategoriesE.PowerDistributionPanelsACDC:
            case ComponentCategoriesE.PowerDistributionPanelsOther:
                return {
                    title: t('quote:components:electrical_material')
                }
            case ComponentCategoriesE.Ballast:
            case ComponentCategoriesE.Rail:
            case ComponentCategoriesE.Triangles:
            case ComponentCategoriesE.ScrewAndBracket:
            case ComponentCategoriesE.CentralEndClamp:
            case ComponentCategoriesE.EndClamp:
            case ComponentCategoriesE.RailClamp:
            case ComponentCategoriesE.Splice:
                return {
                    title: t('quote:components:fixings')
                }
            case ComponentCategoriesE.Other:
                return {
                    title: t('tabs:additional_costs')
                }
            default:
                return {
                    title: t('tabs:other')
                }
        }
    }

    const orderedTitles = [
        'Pannelli fotovoltaici',
        'Inverter',
        'Batteria di accumulo',
        'Fissaggi',
        'Quadri elettrici',
        'Accessori pannelli',
        'Altro',
        'Costi aggiuntivi'
    ]

    const groupedProducts = quoteData?.products?.reduce<{[key: string]: GroupType}>((groups, product) => {
        const {title} = titleToRender(product.product.category.slug)
        if (!groups[title]) {
            groups[title] = {
                title,
                products: []
            }
        }

        groups[title].products.push({
            ...product.product,
            category: product.product.category,
            quantity: Number(product.quantity),
            finalLinePrice: Number(product.finalLinePrice),
            unitPrice: Number(product.unitPrice)
        })
        return groups
    }, {})

    // Sort the groups according to the ordered titles
    const sortedGroups = Object.fromEntries(
        orderedTitles.filter(title => groupedProducts?.[title]).map(title => [title, groupedProducts[title]])
    )

    return (
        <StyledProductsSectionWrapper>
            <StyledSectionTitle>{t('quote:products_section:products')}</StyledSectionTitle>
            <StyledHeader>
                <StyledHeaderTitle>{t('quote:products_section:group')}</StyledHeaderTitle>
                <StyledHeaderTitle>{t('quote:products_section:name')}</StyledHeaderTitle>
                <StyledGroupHeaderItem>
                    {t('quote:products_section:price_per_one_item')}
                    {'*'}
                </StyledGroupHeaderItem>
                <StyledGroupHeaderItem>{t('quote:products_section:quantity')}</StyledGroupHeaderItem>
                <StyledGroupHeaderItem>
                    {t('quote:products_section:price_total')}
                    {'*'}
                </StyledGroupHeaderItem>
            </StyledHeader>
            {Object.values(sortedGroups).map(group => (
                <div key={group.title}>
                    <StyledGroupTitle>{group.title}</StyledGroupTitle>
                    <Divider direction="horizontal" background={`${theme.palette.neutral[300]}`} />
                    {group.products.map(product => (
                        <StyledProductRow key={product.id}>
                            <span />
                            <StyledProductName>{product.name}</StyledProductName>
                            <StyledPrice>
                                {product.unitPrice ? `${formatValues(product.unitPrice, 2)} ${correctCurrency}` : '-'}
                            </StyledPrice>
                            <StyledQuantity>{product.quantity}</StyledQuantity>
                            <StyledPrice>
                                {product.finalLinePrice ? (
                                    <Flexbox align="center" justify="end" gap={2}>
                                        {formatValues(product.finalLinePrice, 2)} {correctCurrency}
                                        {''}
                                    </Flexbox>
                                ) : (
                                    '-'
                                )}
                            </StyledPrice>
                        </StyledProductRow>
                    ))}
                </div>
            ))}
            {/* SERVICES */}
            {!isSimplifiedQuote && (
                <>
                    <StyledGroupTitle>{t('quote:products_section:services')}</StyledGroupTitle>
                    <Divider direction="horizontal" background={`${theme.palette.neutral[300]}`} />
                    {quoteData?.services.map(service => (
                        <StyledProductRow key={service.id}>
                            <span />
                            <StyledProductName>{service?.service?.name}</StyledProductName>
                            <StyledPrice>
                                {service?.unitPrice ? `${formatValues(service?.unitPrice, 2)} ${correctCurrency}` : '-'}
                            </StyledPrice>
                            <StyledQuantity>{service?.quantity}</StyledQuantity>
                            <StyledPrice>
                                {service?.finalLinePrice ? (
                                    <Flexbox align="center" justify="end" gap={2}>
                                        {formatValues(service?.finalLinePrice, 2)} {correctCurrency}
                                        {''}
                                    </Flexbox>
                                ) : (
                                    '-'
                                )}
                            </StyledPrice>
                        </StyledProductRow>
                    ))}
                </>
            )}
            <span>{t('estimation_tab:table_caption')}</span>
            <ProductsTotalPrice quoteData={quoteData} isSimplifiedQuote={isSimplifiedQuote} />
        </StyledProductsSectionWrapper>
    )
}
